/**
 * BIGWA Core - Retry Utility
 * 
 * Funções para retry de operações com exponential backoff.
 */

import { Result, ok, err, AsyncResult } from '../types/result';
import { RetryConfig, DEFAULT_RETRY_CONFIG } from '../types/common';
import { shouldRetry as isRetryableError } from '../errors';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Opções de retry para uma operação específica
 */
export interface RetryOptions extends Partial<RetryConfig> {
  /**
   * Função para determinar se deve tentar novamente
   * Por padrão usa shouldRetry dos erros
   */
  shouldRetry?: (error: Error, attempt: number) => boolean;

  /**
   * Callback chamado antes de cada retry
   */
  onRetry?: (error: Error, attempt: number, delay: number) => void;

  /**
   * Signal para cancelar a operação
   */
  signal?: AbortSignal;
}

/**
 * Resultado de operação com retry incluindo metadados
 */
export interface RetryResult<T> {
  data: T;
  attempts: number;
  totalTime: number;
}

// ============================================================================
// FUNÇÕES PRINCIPAIS
// ============================================================================

/**
 * Executa função com retry e exponential backoff
 * 
 * @example
 * const result = await retry(
 *   () => fetch(url).then(r => r.json()),
 *   { maxRetries: 3, initialDelayMs: 1000 }
 * );
 */
export async function retry<T>(
  fn: () => Promise<T>,
  options?: RetryOptions
): AsyncResult<RetryResult<T>> {
  const config = {
    ...DEFAULT_RETRY_CONFIG,
    ...options,
  };

  const shouldRetryFn = options?.shouldRetry ?? ((error: Error) => isRetryableError(error));
  const startTime = Date.now();
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    // Verifica cancelamento
    if (options?.signal?.aborted) {
      return err(new Error('Operation cancelled'));
    }

    try {
      const data = await fn();
      
      return ok({
        data,
        attempts: attempt + 1,
        totalTime: Date.now() - startTime,
      });
    } catch (error) {
      lastError = error as Error;

      // Última tentativa ou não deve tentar novamente
      if (attempt >= config.maxRetries || !shouldRetryFn(lastError, attempt)) {
        break;
      }

      // Calcula delay com exponential backoff e jitter
      const delay = calculateDelay(attempt, config);

      // Callback de retry
      if (options?.onRetry) {
        options.onRetry(lastError, attempt + 1, delay);
      }

      // Aguarda antes de tentar novamente
      await sleep(delay, options?.signal);
    }
  }

  return err(lastError ?? new Error('Retry failed'));
}

/**
 * Versão simplificada que retorna apenas o valor ou lança exceção
 */
export async function retryOrThrow<T>(
  fn: () => Promise<T>,
  options?: RetryOptions
): Promise<T> {
  const result = await retry(fn, options);
  
  if (result.success) {
    return result.data.data;
  }
  
  throw result.error;
}

/**
 * Cria função com retry embutido
 * 
 * @example
 * const fetchWithRetry = withRetry(
 *   (url: string) => fetch(url).then(r => r.json()),
 *   { maxRetries: 3 }
 * );
 * 
 * const data = await fetchWithRetry('https://api.example.com');
 */
export function withRetry<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  options?: RetryOptions
): (...args: TArgs) => AsyncResult<RetryResult<TReturn>> {
  return (...args: TArgs) => retry(() => fn(...args), options);
}

// ============================================================================
// BATCH RETRY
// ============================================================================

/**
 * Executa múltiplas operações com retry individual
 * 
 * @example
 * const results = await retryAll([
 *   () => fetchUser(1),
 *   () => fetchUser(2),
 *   () => fetchUser(3),
 * ]);
 */
export async function retryAll<T>(
  fns: Array<() => Promise<T>>,
  options?: RetryOptions
): Promise<Array<Result<RetryResult<T>>>> {
  return Promise.all(fns.map((fn) => retry(fn, options)));
}

/**
 * Executa operações em sequência com retry
 * Para na primeira falha
 */
export async function retrySequence<T>(
  fns: Array<() => Promise<T>>,
  options?: RetryOptions
): AsyncResult<RetryResult<T>[]> {
  const results: RetryResult<T>[] = [];

  for (const fn of fns) {
    const result = await retry(fn, options);
    
    if (!result.success) {
      return result;
    }
    
    results.push(result.data);
  }

  return ok(results);
}

// ============================================================================
// UTILITÁRIOS
// ============================================================================

/**
 * Calcula delay com exponential backoff e jitter
 */
function calculateDelay(attempt: number, config: RetryConfig): number {
  // Exponential backoff: initialDelay * 2^attempt
  const exponentialDelay = config.initialDelayMs * Math.pow(config.backoffMultiplier, attempt);
  
  // Aplica limite máximo
  const cappedDelay = Math.min(exponentialDelay, config.maxDelayMs);
  
  // Adiciona jitter (±20%) para evitar thundering herd
  const jitter = cappedDelay * 0.2 * (Math.random() * 2 - 1);
  
  return Math.floor(cappedDelay + jitter);
}

/**
 * Sleep que pode ser cancelado
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, ms);

    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeout);
        reject(new Error('Sleep cancelled'));
      });
    }
  });
}

/**
 * Cria configuração de retry a partir de opções parciais
 */
export function createRetryConfig(options?: Partial<RetryConfig>): RetryConfig {
  return {
    ...DEFAULT_RETRY_CONFIG,
    ...options,
  };
}

/**
 * Configurações pré-definidas de retry
 */
export const RETRY_PRESETS = {
  /**
   * Retry agressivo - muitas tentativas, delays curtos
   */
  AGGRESSIVE: {
    maxRetries: 5,
    initialDelayMs: 500,
    maxDelayMs: 10000,
    backoffMultiplier: 1.5,
  },

  /**
   * Retry conservador - poucas tentativas, delays longos
   */
  CONSERVATIVE: {
    maxRetries: 2,
    initialDelayMs: 2000,
    maxDelayMs: 30000,
    backoffMultiplier: 3,
  },

  /**
   * Retry para operações críticas
   */
  CRITICAL: {
    maxRetries: 10,
    initialDelayMs: 1000,
    maxDelayMs: 60000,
    backoffMultiplier: 2,
  },

  /**
   * Sem retry
   */
  NONE: {
    maxRetries: 0,
    initialDelayMs: 0,
    maxDelayMs: 0,
    backoffMultiplier: 1,
  },
} as const;
